import React, { useContext, useEffect, useState, useRef, Fragment } from "react";
import { Dialog, Disclosure, Transition } from "@headlessui/react";
import { Bars3Icon } from "@heroicons/react/24/outline";
import { useLocation, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import AppContext from "../../context/AppContext";
import { IoIosNotifications, IoMdLogOut } from "react-icons/io";
import { IoSettings } from "react-icons/io5";
import { FiUser } from "react-icons/fi";
import dummyImg from "../../assets/dummy.webp";
import OrganizationFilterSelect from "../common/task/OrganizationFilterSelect";
import { getUnreadMsgCount } from "../../service/Notification";
import Logout from "../common/Logout";
import LangComp from "./langComp";
import DashboardMenus from "./DashboardMenus.js";
import RcOrganization from "../common/task/RcOrganization.js";
import RcActivity from "../common/task/RcActivity.js";
import notification from "../../assets/notification03.png"
import logout from "../../assets/power.png"
import userProfile from "../../assets/user.png"
import { components } from 'react-select';
import ModalCloseButton from "../common/ModalCloseButton";
import { getAllByOrganizationId, getProjectListByDepartmentId } from "../../service/User.js";
import ReactSelector from "../common/CommonReactSelector.js";
import { fetchTasks } from "../../service/Task.js";
import translations from "../common/Translations.js";
import { isRTL } from "../../utils/rtl.js";
import { isOnlyCustomerSupportUser } from "../../utils/permissions";
import { resolveAvatarSrc } from "../../utils/avatar";
import HoverTooltip from "../common/HoverTooltip";

/**
 * Projects shown in nav. Super admins and admins see every project of the
 * department whatever their projectList holds — including an empty list,
 * which is how the user form saves "All projects" (otherwise an "All
 * projects" admin got no project, and project-scoped screens such as User
 * Management never loaded). Other roles: an empty list means none.
 */
const getAccessibleProjectsFlat = (projectByDept, detail) => {
  const projectsFromApi = Object.values(projectByDept || {})?.flat() || [];
  if (!detail) return projectsFromApi;

  const userType = Cookies.get("userType");
  if (userType === "super_admin" || userType === "admin") {
    return projectsFromApi;
  }

  const assignedIds = detail.projectList;
  if (Array.isArray(assignedIds) && assignedIds.length === 0) {
    return [];
  }
  if (!Array.isArray(assignedIds)) {
    return projectsFromApi;
  }
  const idSet = new Set(assignedIds.map(String));
  return projectsFromApi.filter((p) => idSet.has(String(p.id)));
};

const Placeholder = (props) => {
  return <components.Placeholder {...props} />;
};

const getStoredId = (key) => {
  const value = localStorage.getItem(key);
  return value && value !== "null" && value !== "undefined" ? value : null;
};

export const MainNav = () => {
  const [showModal, setShowModal] = useState(false);
  const [userType, setUserType] = useState(null);
  const [isNavbarOpen, setIsNavbarOpen] = useState(false);
  const context = useContext(AppContext);
  const [showProfileModal, setShowProfileModal] = useState(false)
  const [department, setDepartment] = useState([])
  const [departmentsFetched, setDepartmentsFetched] = useState(false);
  const [project, setProject] = useState([])
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [isDepartmentSelected, setIsDepartmentSelected] = useState(false); // State to track department selection
  const {
    theme,
    profilePic,
    setProfilePic,
    workflowName,
    setWorkflowName,
    selectedOrgId,
    setSelectedOrgId,
    setSelectedOrgName,
    selectedOrgName,
    setPriority,
    setActive,
    setSelectedUserId,
    setSelectedUserName,
    fetchNotifications,
    langMode
    , totalUnreadMsgCount, setTotalUnreadMsgCount, toggleLangMode,
    updateProjectId, updateDepartmentId, departmentId, projectId, setTaskData, setWorkflowId, updateActivityId, setIsDcrcEnabled,
    headerActivitySelect
  } = context;
  const user = Cookies.get("userName");
  const isRtl = isRTL(langMode);
  const userDetails = JSON.parse(localStorage.getItem("userDetails"))
  const navbarRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isMapTaskPage = location.pathname === "/maptask";

  const handleReset = () => {
    setActive("Status");
    setPriority("Priority");
    setSelectedUserId('')
    setSelectedUserName("Default")
  };

  const isworkflowpage =
    location.pathname === "/workflow" ||
    location.pathname === "/reports" ||
    location.pathname === "/all-users" ||
    location.pathname === "/export-report-and-data" ||
    location.pathname === "/security" ||
    location.pathname === "/createAccount" ||
    location.pathname === "/stock-managment" ||
    location.pathname === "/inventory-management" ||
    location.pathname === "/export";
  const routesWithoutMainFilters = [
    "/notification",
    "/settings",
  ];
  const shouldHideMainFilters = routesWithoutMainFilters.some((route) =>
    location.pathname === route || location.pathname.startsWith(`${route}/`)
  );

  useEffect(() => {
    const cookieProfilePic = Cookies.get("profilePic");
    setProfilePic(
      resolveAvatarSrc({
        contextProfilePic: cookieProfilePic,
        dummyImg,
      })
    );
    const userTypeCookie = Cookies.get("userType");
    setUserType(userTypeCookie);
  }, []);

  const navAvatarSrc = resolveAvatarSrc({
    contextProfilePic: profilePic,
    dummyImg,
  });

  useEffect(() => {
    const fetchUnreadMsgCount = async () => {
      try {
        const count = await getUnreadMsgCount();
        setTotalUnreadMsgCount(count.body.value.unreadMsgCount);
      } catch (error) {
        console.error("Failed to fetch unread message count:", error);
      }
    };
    fetchUnreadMsgCount();
  }, []);

  const handleLogout = () => setShowModal(true);
  const handleViewNotifications = () => {
    // Clicking the bell while already on the notification page must not overwrite the page to go back to.
    if (location.pathname !== "/notification") Cookies.set("notipreviousPath", location.pathname);
    navigate("/notification");
    setActive("Status");
    setPriority("Priority");
    setSelectedUserId('')
    setSelectedUserName("Default")
  };
  const handleSettings = () => {
    navigate("/settings");
    handleReset();
  };
  const handleProfile = () => {
    Cookies.set("previousPath", location.pathname);
    navigate("/user-profile");
    handleReset();
    setIsNavbarOpen(false);
    setShowProfileModal(false);
  };

  const formatUserType = (userType) => {
    if (!userType) return "Unknown";
    switch (userType) {
      case "super_admin":
        return "Super Admin";
      case "admin":
        return "Admin";
      case "office_user":
        return "Office User";
      default:
        return userType
          .replace(/_/g, " ")
          .replace(/^./, (str) => str.toUpperCase());
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navbarRef.current && !navbarRef.current.contains(event.target)) {
        setIsNavbarOpen(false);
      }
    };

    if (isNavbarOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isNavbarOpen]);

  const userDetail = JSON.parse(localStorage.getItem("userDetails")) || {};
  const isCustomerModule = location.pathname === "/customer-support";

  const hasOtherPrivileges = false;
  const isCustomerSupportOnly =
    isOnlyCustomerSupportUser() && !hasOtherPrivileges;

  const organizationId = String(selectedOrgId || localStorage.getItem("selectedOrgId") || "").replace(
    /"/g,
    ""
  );

  const showDeptProjectFilters =
    !isCustomerModule &&
    !shouldHideMainFilters &&
    !isCustomerSupportOnly;

  const departmentLabel = translations[langMode]?.department ?? "Department";
  const projectLabel = translations[langMode]?.project ?? "Project";
  const activityLabel = translations[langMode]?.Activity ?? "Activity";
  const organizationLabel = translations[langMode]?.inventoryAddOrganizationName ?? "Organization";
  const borderLabelClass = `absolute -top-[7px] z-[1] px-1 text-[11px] font-medium leading-none ${
    isRtl ? "right-3" : "left-3"
  } ${
    theme === "dark"
      ? "bg-[#1e293b] text-slate-300"
      : theme === "high-contrast"
      ? "bg-black text-white"
      : "bg-white text-gray-600"
  }`;

  const renderDepartmentProjectSelectors = (
    wrapperClass = "",
    showOrganization = true,
    selectorClass = "w-full min-w-0 sm:flex-1",
    forceStacked = false
  ) => (
    <div
      className={`flex flex-col ${
        forceStacked ? "gap-4" : "gap-4 sm:flex-row sm:gap-2"
      } w-full min-w-0 max-w-full pt-2 ${
        isMapTaskPage && !forceStacked ? "overflow-visible" : "overflow-x-hidden"
      } ${wrapperClass}`}
    >
      {showOrganization && (
        <div className={`${selectorClass} relative min-w-0 max-w-full`}>
          <span className={`${borderLabelClass} pointer-events-none`}>{organizationLabel}</span>
          <OrganizationFilterSelect
            selectedOrgName={selectedOrgName}
            setSelectedOrgId={setSelectedOrgId}
            setSelectedOrgName={setSelectedOrgName}
            setWorkflowName={setWorkflowName}
            width="full"
          />
        </div>
      )}
      <div className={`${selectorClass} relative min-w-0 max-w-full`}>
        <label htmlFor="department-select" className={borderLabelClass}>
          {departmentLabel}
        </label>
        <ReactSelector
          value={departmentOptions.find((d) => String(d.value) === String(departmentId)) || null}
          options={departmentOptions}
          components={Placeholder}
          placeholder={departmentLabel}
          onChange={handleDepartmentChange}
          height="42px"
          menuPortalTarget={document.body}
          dataTestId="department-select"
        />
      </div>
      <div className={`${selectorClass} relative min-w-0 max-w-full`}>
        <label htmlFor="project-select" className={borderLabelClass}>
          {projectLabel}
        </label>
        {accessibleProjectsFlat.length > 0 ? (
          <ReactSelector
            value={options.find((p) => String(p.value) === String(projectId)) || null}
            options={options}
            components={Placeholder}
            placeholder={projectLabel}
            onChange={handleProjectChange}
            height="42px"
            menuPortalTarget={document.body}
            dataTestId="project-select"
          />
        ) : (
          <div className="flex h-9 items-center rounded-md border border-gray-200 bg-gray-50 px-3 text-sm text-gray-500">
            {translations[langMode]?.noproject ?? "No projects available"}
          </div>
        )}
      </div>

      {headerActivitySelect && (
        <div
          className={`${selectorClass} relative min-w-0 max-w-full`}
          data-testid="import-csv-workflow-select"
        >
          <span className={`${borderLabelClass} pointer-events-none`}>{activityLabel}</span>
          {headerActivitySelect}
        </div>
      )}
      {isMapTaskPage && !showOrganization && (
        <div className={`${selectorClass} relative min-w-0 max-w-full`}>
          <span className={`${borderLabelClass} pointer-events-none`}>{activityLabel}</span>
          <div id="map-activity-header-slot" className="w-full" />
        </div>
      )}
      {isMapTaskPage && forceStacked && (
        <div className={`${selectorClass} relative min-w-0 max-w-full`}>
          <span className={`${borderLabelClass} pointer-events-none`}>{activityLabel}</span>
          <div id="map-activity-drawer-slot" className="w-full min-w-0" />
        </div>
      )}
      {isMapTaskPage && !showOrganization && (
        <div className={`${selectorClass} min-w-0 max-w-full relative`}>
          <div id="map-calendar-header-trigger-slot" className="w-full" />
          <div id="map-calendar-header-panel-slot" className="absolute right-0 top-full mt-2 z-[1400]" />
        </div>
      )}
    </div>
  );

  useEffect(() => {
    const storedDepartmentId = getStoredId("departmentId");
    const storedProjectId = getStoredId("projectId");

    if (storedDepartmentId) {
      updateDepartmentId(Number(storedDepartmentId));
    }
    if (storedProjectId) {
      updateProjectId(Number(storedProjectId));
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setDepartmentsFetched(false);
    const fetchDepartments = async () => {
      if (!organizationId) return;
      try {
        const data = await getAllByOrganizationId(organizationId);
        if (cancelled) return;
        setDepartment(data);
        setDepartmentsFetched(true);
        const storedDepartmentId = getStoredId("departmentId");
        if (storedDepartmentId && data.some((item) => String(item.id) === String(storedDepartmentId))) {
          updateDepartmentId(Number(storedDepartmentId));
        } else {
          updateDepartmentId(data[0]?.id ?? null);
          updateProjectId(null);
          updateActivityId(null);
          setProject([]);
        }
      } catch (error) {
        if (cancelled) return;
        setDepartment([]);
        console.error("Error fetching departments:", error);
        setDepartmentsFetched(true);
      }
    };
    fetchDepartments();
    return () => { cancelled = true; };
  }, [organizationId]);

  const departmentOptions = Array.isArray(department)
    ? department.map((item) => ({ value: item.id, label: item.name }))
    : [];
  const defaultDepartment = departmentOptions.find((d) => String(d.value) === String(departmentId));
  const handleDepartmentChange = async (selectedOption) => {
    try {
      const departmentId = selectedOption.value;
      localStorage.setItem("departmentId", selectedOption.value);

      if (departmentId) {
        const projects = await getProjectListByDepartmentId(departmentId);
        const data = await fetchTasks({ workflowIds: '', priority: "Priority", active: "Status", currentPage: 0, pageSize: 10 });
        setTaskData(data?.taskList || [])
        setWorkflowId(null)
        updateDepartmentId(selectedOption.value)
        updateActivityId(null)
        setProject(projects)
        setIsDepartmentSelected(true);
        updateProjectId(null);
        localStorage.removeItem("activityId");
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      setIsDepartmentSelected(false);
    }
  };

  const hasFetched = useRef(false);

  const fetchProjectList = async () => {
    try {
      if (departmentId) {
        const data = await getProjectListByDepartmentId(departmentId);
        setProject(data);
        const storedProjectId = getStoredId("projectId");
        const accessible = getAccessibleProjectsFlat(data, userDetail);
        if (accessible.length === 0) {
          updateProjectId(null);
          localStorage.removeItem("projectId");
        } else {
          const storedValid =
            storedProjectId &&
            accessible.some((p) => String(p.id) === String(storedProjectId));
          if (storedValid) {
            updateProjectId(Number(storedProjectId));
          } else {
            const firstProject = accessible[0];
            updateProjectId(firstProject.id);
            localStorage.setItem("projectId", firstProject.id);
          }
        }
      } else {
        setProject([]);
      }
    } catch (error) {
      setProject([]);
      console.error("Error fetching projects:", error);
    }
  };

  useEffect(() => {
    hasFetched.current = false;
  }, [organizationId, departmentId]);

  useEffect(() => {
    if (!departmentsFetched) return;
    if ((department || []).length === 0) {
      setProject([]);
      localStorage.removeItem("activityId");
      setTimeout(() => {
        updateActivityId(null);
      }, 300);
    }
    if ((department || []).length > 0 && departmentId && !hasFetched.current) {
      fetchProjectList();
      hasFetched.current = true;
    }
  }, [department, departmentId, departmentsFetched]);

  const accessibleProjectsFlat = getAccessibleProjectsFlat(project, userDetail);
  const options = accessibleProjectsFlat?.map((item) => ({ "value": item.id, "label": item.name }));
  const projectAssignmentKey = Array.isArray(userDetail.projectList)
    ? userDetail.projectList.join(",")
    : "_unset";

  useEffect(() => {
    if (!projectId || !accessibleProjectsFlat.length) {
      setIsDcrcEnabled?.(false);
      return;
    }
    const selectedProject = accessibleProjectsFlat.find((p) => String(p.id) === String(projectId));
    setIsDcrcEnabled?.(selectedProject?.isDcrcEnabled ?? false);
  }, [projectId, project, projectAssignmentKey]);

  const handleProjectChange = async (selectedOption) => {
    try {
      const projectId = selectedOption?.value;
      localStorage.setItem("projectId", selectedOption.value); // Save to localStorage
      if (projectId) {
        updateProjectId(projectId)
        setIsDepartmentSelected(false)
        updateActivityId(null)
        setTaskData([])
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  };

  return (
    <>
      <Disclosure
        as="nav"
        ref={navbarRef}
        className={`bg-white w-full rounded-[16px] border border-gray-200/50 shadow-sm ${theme === "dark" ? "dark-mode" : ""
          }`}
      >
        {({ open }) => (
          <>
            <div className={`w-full px-3 py-1 sm:pr-0 sm:px-4 lg:px-4`}>
              <div className="flex min-h-16 items-center justify-between lg:justify-end lg:space-x-0">

                <div
                  className="z-50 block lg:hidden"
                >
                  <DashboardMenus />
                </div>
                <div className={`hidden lg:flex md:items-center gap-2 w-full min-w-0 ${isCustomerModule || !showDeptProjectFilters ? "justify-end" : "justify-between"}`}>
                  {showDeptProjectFilters && renderDepartmentProjectSelectors(
                    `flex-1 min-w-0 ${isRtl ? "ms-2" : "me-2"}`,
                    false,
                    "w-[220px] min-w-[220px]"
                  )}
                  <div className="flex shrink-0 items-center justify-end gap-2 ml-1">
                    {isMapTaskPage && (
                      <div id="map-export-header-slot" className="shrink-0" />
                    )}
                    <HoverTooltip text={translations[langMode]?.notification ?? "Notification"} position="bottom">
                    <div
                      className="relative group w-[44px] h-[44px] rounded-full bg-[#EBECEF] cursor-pointer"
                      onClick={handleViewNotifications}
                    >
                      <img
                        src={notification}
                        alt="Notifications"
                        className="absolute top-[11px] left-[11px] h-[22px] w-[22px] filter brightness-75 group-hover:brightness-100"
                      />
                      <span className="absolute top-0 right-0 transform translate-x-1/2 -translate-y-1/2 bg-[#EF4A00] text-white text-xs font-semibold rounded-full w-[18px] h-[18px] flex items-center justify-center shadow-md">
                        {totalUnreadMsgCount}
                      </span>
                    </div>
                    </HoverTooltip>
                    <LangComp toggleLangMode={toggleLangMode} isNavbarOpen={isNavbarOpen} />
                    <div className="relative " data-testid="profile-avatar">
                      <img
                        className={`w-10 h-10 p-1 rounded-full cursor-pointer ring-2 ring-gray-300 ${theme === "dark"
                          ? "bg-white"
                          : theme === "high-contrast"
                            ? "bg-white"
                            : ""
                          }`}
                        src={navAvatarSrc}
                        alt="Avatar"
                        onClick={() => {
                          Cookies.set("previousPath", location.pathname);
                          setShowProfileModal(!showProfileModal)
                          setActive("Status")
                          setPriority("Priority")
                          setSelectedUserId('')
                          setSelectedUserName("Default")
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className="lg:hidden inline-flex items-center gap-4 justify-center">
                  {isNavbarOpen ? (
                    <ModalCloseButton
                      onClick={() => setIsNavbarOpen(false)}
                      ariaLabel="Close main menu"
                      size={24}
                      className="text-gray-400"
                    />
                  ) : (
                    <Disclosure.Button
                      onClick={() => setIsNavbarOpen(true)}
                      className="inline-flex items-center justify-center p-2 text-gray-400 hover:text-red-600"
                    >
                      <span className="sr-only">Open main menu</span>
                      <Bars3Icon className="h-6 w-6" />
                    </Disclosure.Button>
                  )}
                </div>
              </div>
            </div>
            <Disclosure.Panel
              className={`lg:hidden fixed inset-0 z-50 bg-black/50 transition-opacity duration-300 ease-in-out ${
                isNavbarOpen
                  ? "opacity-100 pointer-events-auto"
                  : "opacity-0 pointer-events-none"
              }`}
              static
              >
                <div
                  className={`absolute top-0 ${
                  isRtl ? "left-0" : "right-0"
                } h-[100dvh] w-[88vw] max-w-[340px] sm:w-[420px] sm:max-w-[420px] min-w-0 bg-white shadow-2xl overflow-y-auto overflow-x-hidden mobile-hide-scrollbar transform transition-transform duration-300 ease-in-out ${
                  isNavbarOpen
                    ? "translate-x-0"
                    : isRtl
                    ? "-translate-x-full"
                    : "translate-x-full" 
                }`}
              >
                <div className="w-full min-w-0 max-w-full overflow-x-hidden mobile-hide-scrollbar p-4 sm:p-5 flex min-h-full flex-col gap-4">
                  <ModalCloseButton
                    onClick={() => setIsNavbarOpen(false)}
                    ariaLabel="Close mobile menu"
                    className="self-end"
                  />
                  {isCustomerSupportOnly ? (
                    <>
                      <RcOrganization />
                      <RcActivity />
                    </>
                  ) : (
                    <>
                      {showDeptProjectFilters && (
                        <>
                          {renderDepartmentProjectSelectors(
                            "",
                            true,
                            "w-full min-w-0",
                            true
                          )}
                          <LangComp
                            toggleLangMode={toggleLangMode}
                            isNavbarOpen={isNavbarOpen}
                          />
                        </>
                      )}</>
                  )}
                  {isMapTaskPage && (
                    <div className="flex w-full min-w-0 max-w-full flex-col gap-3 border-t border-gray-100 pt-3">
                      <span className="text-xs font-semibold uppercase text-gray-400">
                        {translations[langMode]?.mapToolsLabel ?? "Map Tools"}
                      </span>
                      {!showDeptProjectFilters && (
                        <div id="map-activity-drawer-slot" className="w-full min-w-0" />
                      )}
                      <div id="map-calendar-drawer-trigger-slot" className="w-full min-w-0 relative" />
                      <div id="map-export-drawer-slot" className="w-full min-w-0" />
                    </div>
                  )}
                  <div className="mt-3 flex w-full min-w-0 max-w-full flex-col gap-3">
                    <div className="grid w-full grid-cols-[40px_minmax(0,1fr)] items-center gap-3 rounded-md p-2">
                      <div className="relative h-10 w-10">
                        <img
                          className={`h-10 w-10 rounded-full p-1 cursor-pointer ring-2 ring-gray-30 ${theme === "dark"
                            ? "bg-white"
                            : theme === "high-contrast"
                              ? "bg-white"
                              : ""
                          }`}
                          src={navAvatarSrc}
                          alt="Avatar"
                          onClick={handleProfile}
                        />
                        <span className="absolute top-0 right-0 w-3.5 h-3.5 bg-green-400 rounded-full border border-white" />
                      </div>
                      <div className="min-w-0">
                        <span
                          className={` ${theme === "dark"
                            ? "dark-mode "
                            : theme === "high-contrast"
                              ? "high-contrast  "
                              : ""
                            } block truncate text-sm font-bold`}
                        >
                          {user}
                        </span>
                        <span
                          className={`block truncate text-xs text-gray-700 ${theme === "dark"
                            ? "dark-mode "
                            : theme === "high-contrast"
                              ? "  "
                              : ""
                            }`}
                        >
                          {formatUserType(userType)}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="grid w-full grid-cols-[40px_minmax(0,1fr)] items-center gap-3 rounded-md p-2 text-start text-gray-800 transition-colors hover:bg-gray-100"
                      data-testid="mobile-menu-my-profile"
                      onClick={handleProfile}
                    >
                      <span className="flex h-10 w-10 items-center justify-center">
                        <FiUser className="h-6 w-6 text-gray-600" />
                      </span>
                      <span className="truncate text-sm font-medium">
                        {translations[langMode]?.myprofile || "My Profile"}
                      </span>
                    </button>
                    <div
                      className="grid w-full grid-cols-[40px_minmax(0,1fr)] items-center gap-3 rounded-md p-2 hover:bg-gray-100 cursor-pointer transition-colors"
                      onClick={handleViewNotifications}>
                      <span className="flex h-10 w-10 items-center justify-center">
                        <IoIosNotifications className="h-6 w-6 text-gray-600" />
                      </span>
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate text-sm font-medium">{translations[langMode]?.sidebarNotifications ?? "Notifications"}</span>
                        <span className="truncate text-xs text-gray-500">
                          {(translations[langMode]?.sidebarUnreadCount ?? "{count} unread").replace("{count}", totalUnreadMsgCount)}
                        </span>
                      </div>
                    </div>
                    <div
                      className="grid w-full grid-cols-[40px_minmax(0,1fr)] items-center gap-3 rounded-md p-2 hover:bg-gray-100 cursor-pointer transition-colors"
                      onClick={handleSettings}>
                      <span className="flex h-10 w-10 items-center justify-center">
                        <IoSettings className="h-6 w-6 text-gray-600" />
                      </span>
                      <span className="truncate text-sm font-medium">{translations[langMode]?.sidebarSettings ?? "Settings"}</span>
                    </div>
                    <div
                      className="grid w-full grid-cols-[40px_minmax(0,1fr)] items-center gap-3 rounded-md p-2 hover:bg-gray-100 cursor-pointer transition-colors"
                      data-testId='logout'
                      onClick={handleLogout} >
                      <span className="flex h-10 w-10 items-center justify-center">
                        <IoMdLogOut className="h-6 w-6 text-gray-600" />
                      </span>
                      <span className="truncate text-sm font-medium">{translations[langMode]?.Logout?.trim() ?? "Logout"}</span>
                    </div>
                  </div>
                </div>
              </div>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
      <Transition.Root show={showProfileModal} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setShowProfileModal(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity z-50" />
          </Transition.Child>
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                data-testid="profile-menu"
                className={`absolute transform rounded-lg shadow-xl bg-white flex flex-col gap-[16px] !max-w-[312px] !w-full h-auto !top-[7%] ${isRtl ? '!left-[2%]' : '!right-[2%]'} py-[12px] px-[16px]`} >
                <div className=" flex flex-col gap-[3px]"
                  style={{
                    background: "linear-gradient(90deg, #E7F9FF 0%, #F2F7FD 100%)",
                    padding: "12px 16px",
                    borderRadius: "8px"
                  }}>
                  <div className="flex justify-between w-full">
                    <span
                      style={{
                        fontSize: "18px",
                        fontWeight: "600"
                      }}
                      className={` ${theme === "dark"
                        ? "dark-mode "
                        : theme === "high-contrast"
                          ? "high-contrast  "
                          : ""
                        } font-bold text-sm`}
                    >
                      {user}
                    </span>
                    <ModalCloseButton
                      onClick={() => setShowProfileModal(!showProfileModal)}
                    />
                  </div>
                  <span
                    style={{
                      textAlign: "start",
                    }}
                    className={`block text-gray-700 text-xs font-medium  ${theme === "dark"
                      ? "dark-mode "
                      : theme === "high-contrast"
                        ? "  "
                        : ""
                      }`}>
                    {formatUserType(userType)}
                  </span>
                </div>
                {showDeptProjectFilters && (
                  <div className="relative w-full min-w-0">
                    <span className={`${borderLabelClass} pointer-events-none`}>{organizationLabel}</span>
                    <OrganizationFilterSelect
                      selectedOrgName={selectedOrgName}
                      setSelectedOrgId={setSelectedOrgId}
                      setSelectedOrgName={setSelectedOrgName}
                      setWorkflowName={setWorkflowName}
                      width="full"
                    />
                  </div>
                )}
                <div className="relative group flex gap-[10px] cursor-pointer" data-testid="profile-menu-my-profile" onClick={() => { navigate("/user-profile"); setTimeout(() => setShowProfileModal(false), 100) }}>
                  <img src={userProfile} className="filter brightness-75 hover:text-blue-500" />
                  <div className="text-[#111827] text-base font-normal hover:text-[#4B5563]">
                    {translations[langMode].myprofile}
                    <div className="tooltip-arrow" data-popper-arrow></div>
                  </div>
                </div>
                <div className="relative group flex gap-[10px] cursor-pointer" data-testid="profile-menu-logout" onClick={handleLogout}>
                  <img src={logout} className="filter brightness-75 hover:text-blue-500" />
                  <div className="text-[#111827] font-normal text-base hover:text-[#4B5563]">
                    {translations[langMode].Logout}
                    <div className="tooltip-arrow" data-popper-arrow></div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
      <Logout setShowModal={setShowModal} showModal={showModal} isNavbarOpen={isNavbarOpen} />
    </>
  );
};
